import { jsPDF } from 'jspdf';
import { APP_NAME } from '../constants';
import {
  formatFrenchDate,
  getQuoteProblemName,
  getQuoteServiceName,
} from '../pages/public-service-tracking/appointmentTiming';

const formatPrice = (price) =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: 'EUR',
  }).format(Number(price) || 0);

const safeText = (value, fallback = '—') => {
  const text = `${value ?? ''}`.trim();
  return text || fallback;
};

/**
 * Construit un PDF A4 du devis (appointment + appointment_quote).
 */
export function buildQuotePdf(appointment, quote) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const margin = 18;
  let y = 22;

  const problemName = getQuoteProblemName(appointment);
  const serviceName = getQuoteServiceName(appointment);
  const createdAt = formatFrenchDate(quote?.created_at || quote?.createdAt);
  const validUntil = formatFrenchDate(quote?.valid_until || quote?.validUntil);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text(APP_NAME, margin, y);
  y += 8;

  doc.setFontSize(20);
  doc.text('Devis', margin, y);
  y += 10;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.text(`Reference devis : ${safeText(quote?.reference)}`, margin, y);
  y += 6;
  if (appointment?.reference) {
    doc.text(`Reference demande : ${appointment.reference}`, margin, y);
    y += 6;
  }
  if (createdAt) {
    doc.text(`Date d'emission : ${createdAt}`, margin, y);
    y += 6;
  }
  if (validUntil) {
    doc.text(`Valable jusqu'au : ${validUntil}`, margin, y);
    y += 6;
  }

  y += 4;
  doc.setDrawColor(200);
  doc.line(margin, y, 210 - margin, y);
  y += 10;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Client', margin, y);
  y += 7;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.text(safeText(appointment?.customerName || appointment?.customer_name), margin, y);
  y += 6;
  if (appointment?.customerEmail || appointment?.customer_email) {
    doc.text(safeText(appointment?.customerEmail || appointment?.customer_email), margin, y);
    y += 6;
  }
  if (appointment?.customerPhone || appointment?.customer_phone) {
    doc.text(safeText(appointment?.customerPhone || appointment?.customer_phone), margin, y);
    y += 6;
  }
  if (appointment?.customerAddress || appointment?.customer_address) {
    const addressLines = doc.splitTextToSize(
      safeText(appointment?.customerAddress || appointment?.customer_address),
      174
    );
    doc.text(addressLines, margin, y);
    y += addressLines.length * 6;
  }

  y += 4;
  doc.setDrawColor(200);
  doc.line(margin, y, 210 - margin, y);
  y += 10;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Objet du devis', margin, y);
  y += 7;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  if (problemName) {
    doc.text(`Probleme : ${problemName}`, margin, y);
    y += 6;
  }
  if (serviceName) {
    doc.text(`Solution : ${serviceName}`, margin, y);
    y += 6;
  }

  const notes = `${quote?.notes || ''}`.trim();
  if (notes) {
    y += 4;
    doc.setFont('helvetica', 'bold');
    doc.text('Conditions / notes', margin, y);
    y += 7;
    doc.setFont('helvetica', 'normal');
    const noteLines = doc.splitTextToSize(notes, 174);
    doc.text(noteLines, margin, y);
    y += noteLines.length * 6 + 4;
  }

  y += 4;
  doc.setDrawColor(200);
  doc.line(margin, y, 210 - margin, y);
  y += 10;

  const price = Number(quote?.price) || 0;
  const discountType = quote?.discount_type || quote?.discountType || 'percentage';
  const discountValue = Number(quote?.discount_value ?? quote?.discountValue) || 0;
  const totalHt = Number(quote?.total_ht ?? quote?.totalHt);
  const vatRate = Number(quote?.vat_rate ?? quote?.vatRate) || 0;
  const totalTtc = Number(quote?.total_ttc ?? quote?.totalTtc);

  const computedDiscount =
    discountType === 'percentage' ? price * (discountValue / 100) : discountValue;
  const ht = Number.isFinite(totalHt) ? totalHt : Math.max(price - computedDiscount, 0);
  const vat = ht * (vatRate / 100);
  const ttc = Number.isFinite(totalTtc) ? totalTtc : ht + vat;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text('Montants', margin, y);
  y += 8;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);

  const rows = [
    ['Prix HT', formatPrice(price)],
    [
      discountValue > 0
        ? `Remise (${discountType === 'percentage' ? `${discountValue} %` : formatPrice(discountValue)})`
        : 'Remise',
      discountValue > 0 ? `- ${formatPrice(computedDiscount)}` : formatPrice(0),
    ],
    ['Total HT', formatPrice(ht)],
    [`TVA (${vatRate} %)`, formatPrice(vat)],
    ['Total TTC', formatPrice(ttc)],
  ];

  rows.forEach(([label, value], index) => {
    const isTotal = index === rows.length - 1;
    if (isTotal) {
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(12);
    }
    doc.text(label, margin, y);
    doc.text(value, 210 - margin, y, { align: 'right' });
    y += isTotal ? 8 : 7;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
  });

  y += 12;
  doc.setFontSize(9);
  doc.setTextColor(100);
  doc.text(
    'Document genere automatiquement. Conservez ce devis pour vos echanges.',
    margin,
    Math.min(y, 280)
  );
  doc.setTextColor(0);

  return doc;
}

export function getQuotePdfFilename(quote) {
  const ref = `${quote?.reference || 'devis'}`.replace(/[^a-zA-Z0-9-_]/g, '_');
  return `${ref}.pdf`;
}

export function createQuotePdfBlobUrl(appointment, quote) {
  const doc = buildQuotePdf(appointment, quote);
  return URL.createObjectURL(doc.output('blob'));
}

/** Base64 PDF (sans préfixe data:) pour envoi email Resend. */
export function getQuotePdfBase64(appointment, quote) {
  const doc = buildQuotePdf(appointment, quote);
  return doc.output('datauristring').split(',')[1] || '';
}

export function downloadQuotePdf(appointment, quote) {
  const doc = buildQuotePdf(appointment, quote);
  doc.save(getQuotePdfFilename(quote));
}
